import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import sucursalesVentaService, { SucursalVenta } from '../services/sucursalesVentaService';
import sucursalXUsuarioService, { SucursalXUsuario } from '../services/sucursalXUsuarioService';
import { sanitizeGlobalSucursalCompaniaPrefs } from '../utils/companiaPreferida';
import { isAdminRole } from '../utils/ubicacionUsuarioRoles';

const mapAsignacionToSucursal = (item: SucursalXUsuario): SucursalVenta => ({
  nroSuc: item.nroSuc,
  nombreSucursal: item.nombreSucursal ?? `Sucursal ${item.nroSuc}`,
  idCompania: item.idCompania ?? null,
});

export interface SucursalesUsuarioState {
  sucursales: SucursalVenta[];
  preferida: string | null;
  preferidaNro: number | null;
  preferidaSucursal: SucursalVenta | null;
  loading: boolean;
  error: string | null;
  sinAsignaciones: boolean;
  esAdmin: boolean;
  refresh: () => Promise<void>;
}

export function useSucursalesUsuario(): SucursalesUsuarioState {
  const { user } = useAuth();
  const esAdmin = useMemo(() => isAdminRole(user?.roles), [user?.roles]);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [preferidaNro, setPreferidaNro] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sinAsignaciones, setSinAsignaciones] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await sucursalXUsuarioService.getMias();
      const items = data.items ?? [];
      const mapped = items.map(mapAsignacionToSucursal);
      setSucursales(mapped);
      sanitizeGlobalSucursalCompaniaPrefs(mapped);
      const fromFlag = items.find((i) => i.preferida)?.nroSuc;
      const preferida =
        data.preferidaNroSuc != null && Number(data.preferidaNroSuc) > 0
          ? Number(data.preferidaNroSuc)
          : fromFlag != null && Number(fromFlag) > 0
            ? Number(fromFlag)
            : null;
      setPreferidaNro(preferida);
      setSinAsignaciones(!data.esAdmin && data.sinAsignaciones);
    } catch (err: unknown) {
      if (esAdmin) {
        try {
          const result = await sucursalesVentaService.getSucursalesVenta({
            pageNumber: 1,
            pageSize: 500,
          });
          const adminItems = result.items ?? [];
          setSucursales(adminItems);
          sanitizeGlobalSucursalCompaniaPrefs(adminItems);
          setPreferidaNro(null);
          setSinAsignaciones(false);
          setError(null);
          return;
        } catch {
        }
      }
      setSucursales([]);
      setPreferidaNro(null);
      setSinAsignaciones(!esAdmin);
      setError(err instanceof Error ? err.message : 'Error al cargar sucursales del usuario');
    } finally {
      setLoading(false);
    }
  }, [esAdmin]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const preferida = useMemo(() => {
    if (preferidaNro == null) return null;
    const found = sucursales.find((s) => s.nroSuc === preferidaNro);
    return found
      ? `${found.nroSuc} - ${found.nombreSucursal?.trim() || `Sucursal ${found.nroSuc}`}`
      : null;
  }, [preferidaNro, sucursales]);

  const preferidaSucursal = useMemo(() => {
    if (preferidaNro == null) return null;
    return sucursales.find((s) => Number(s.nroSuc) === Number(preferidaNro)) ?? null;
  }, [preferidaNro, sucursales]);

  return {
    sucursales,
    preferida,
    preferidaNro,
    preferidaSucursal,
    loading,
    error,
    sinAsignaciones,
    esAdmin,
    refresh,
  };
}
